import axios from "axios";
import Datasource from "lib/datasource";
import { memoAC } from "./memoize";
import { getAuthTokenFromCookie } from "lib/hooks";

const headers = () => ({
	"Content-Type": "application/json",
	Authorization: `Bearer ${getAuthTokenFromCookie()}`
});

export const getForms = memoAC((params: { propertyId?: string; companyId?: string; q?: string; category?: string; perPage?: number } = {}) => (dispatch: any) => {
	dispatch({ type: "GET_FORMS_REQUEST" });
	const query: string[] = [];
	if (params.propertyId) query.push(`property_id=${params.propertyId}`);
	if (params.companyId) query.push(`company_id=${params.companyId}`);
	if (params.q) query.push(`q=${encodeURIComponent(params.q)}`);
	if (params.category) query.push(`category=${encodeURIComponent(params.category)}`);
	// per_page=-1 tells the API to return every record (TcpPaginator). Used by the
	// form picker so no form is dropped by the default page size.
	if (params.perPage != null) query.push(`per_page=${params.perPage}`);
	const qs = query.length ? `?${query.join("&")}` : "";
	axios.get(`${process.env.REACT_APP_API_ENDPOINT_V2}forms${qs}`, { headers: headers() })
		.then((res) => {
			const forms = Datasource.getNormalizedData(res.data, "form");
			dispatch({ type: "GET_FORMS_SUCCESS", response: { status: 200 }, resource: forms });
		})
		.catch((e) => { dispatch({ type: "GET_FORMS_FAILED", response: e }); });
});

export const getForm = memoAC((id: string) => (dispatch: any) => {
	dispatch({ type: "GET_FORM_REQUEST" });
	axios.get(`${process.env.REACT_APP_API_ENDPOINT_V2}forms/${id}`, { headers: headers() })
		.then((res) => {
			const form = Datasource.getNormalizedData(res.data, "form");
			dispatch({ type: "GET_FORM_SUCCESS", response: { status: 200 }, resource: form });
		})
		.catch((e) => { dispatch({ type: "GET_FORM_FAILED", response: e }); });
});

export const postForm = memoAC((form: any) => (dispatch: any) => {
	dispatch({ type: "POST_FORM_REQUEST" });
	return axios.post(`${process.env.REACT_APP_API_ENDPOINT_V2}forms`, { form }, { headers: headers() })
		.then((res) => {
			const newForm = Datasource.getNormalizedData(res.data, "form");
			dispatch({ type: "POST_FORM_SUCCESS", response: { status: 201 }, resource: newForm });
			return newForm;
		})
		.catch((e) => { dispatch({ type: "POST_FORM_FAILED", response: e }); throw e; });
});

export const putForm = memoAC((form: any) => (dispatch: any) => {
	dispatch({ type: "PUT_FORM_REQUEST" });
	return axios.put(`${process.env.REACT_APP_API_ENDPOINT_V2}forms/${form.id}`, { form }, { headers: headers() })
		.then((res) => {
			const updated = Datasource.getNormalizedData(res.data, "form");
			dispatch({ type: "PUT_FORM_SUCCESS", response: { status: 200 }, resource: updated });
			return updated;
		})
		.catch((e) => { dispatch({ type: "PUT_FORM_FAILED", response: e }); throw e; });
});

/**
 * CANONICAL VIEW-TRACKING CALL for Forms.
 * Records a server-side "View" Activity for the given form.
 * Server applies a 5-minute dedupe per (user, form); this thunk
 * fires once per form open. Failures are swallowed (console only)
 * so the user never sees an error from a tracking call.
 *
 * F8: This is the ONLY correct way to emit a View event for a Form.
 * DO NOT use postEvent("View", form) anywhere in Form Show pages.
 * postEvent bypasses server-side dedupe and will double-log.
 * Contract: api-contract.md §2.5, §2.10.
 */
export const recordFormView = (formId: string) => (dispatch: any) => {
	dispatch({ type: "RECORD_FORM_VIEW_REQUEST", formId });
	return axios.post(
		`${process.env.REACT_APP_API_ENDPOINT_V2}forms/${formId}/record-view`,
		{},
		{ headers: headers() }
	)
		.then((res) => {
			dispatch({ type: "RECORD_FORM_VIEW_SUCCESS", formId, status: res.status });
		})
		.catch((e) => {
			// eslint-disable-next-line no-console
			console.warn(`recordFormView failed for ${formId}:`, e?.response?.status, e?.message);
			dispatch({ type: "RECORD_FORM_VIEW_FAILED", formId, response: e });
		});
};

/**
 * Presigned POST for a NEW form's .docx, before any Form exists (Sim, 24 Sep
 * 2026). The Forms tab uploads the file first and only then creates the Form
 * with the returned key, so a failed upload leaves no fileless Form behind —
 * the same upload-first rule as actions/documents#createDocumentUploadFirst.
 */
export const getFormUploadUrl = () => (dispatch: any) => {
	dispatch({ type: "FORM_UPLOAD_URL_REQUEST" });
	return axios.post(`${process.env.REACT_APP_API_ENDPOINT_V2}forms/upload_url`, {}, { headers: headers() })
		.then((res) => {
			dispatch({ type: "FORM_UPLOAD_URL_SUCCESS" });
			return res.data?.presigned_post_url;
		})
		.catch((e) => { dispatch({ type: "FORM_UPLOAD_URL_FAILED", response: e }); throw e; });
};

/**
 * Presigned POST for REPLACING a form's document.
 *
 * Deliberately a separate call from the `presigned_post_url` the serializer
 * hands out on create: that one reuses the form's existing S3 key, and a
 * snapshot shares its template's key rather than a copy of the bytes, so
 * overwriting it would rewrite the document under every condition that ever
 * used this form. The server mints a fresh key here instead.
 */
export const getFormReplaceUploadUrl = (id: string) => (dispatch: any) => {
	dispatch({ type: "FORM_REPLACE_URL_REQUEST", id });
	return axios.post(
		`${process.env.REACT_APP_API_ENDPOINT_V2}forms/${id}/replace-upload-url`,
		{},
		{ headers: headers() }
	)
		.then((res) => {
			dispatch({ type: "FORM_REPLACE_URL_SUCCESS", id });
			return res.data?.presigned_post_url;
		})
		.catch((e) => { dispatch({ type: "FORM_REPLACE_URL_FAILED", response: e }); throw e; });
};

/**
 * Commits the replacement: swaps the document behind the form, leaving its
 * name, code, category and description alone. The server re-extracts the
 * placeholder and checkbox lists from the new file.
 */
export const replaceFormDocument = (form: { id: string; file_uri: string; mimetype?: string; filesize_bytes?: number }) => (dispatch: any) => {
	dispatch({ type: "REPLACE_FORM_REQUEST" });
	return axios.put(`${process.env.REACT_APP_API_ENDPOINT_V2}forms/${form.id}/replace`, { form }, { headers: headers() })
		.then((res) => {
			const updated = Datasource.getNormalizedData(res.data, "form");
			dispatch({ type: "PUT_FORM_SUCCESS", response: { status: 200 }, resource: updated });
			return updated;
		})
		.catch((e) => { dispatch({ type: "REPLACE_FORM_FAILED", response: e }); throw e; });
};

export const deleteForm = memoAC((id: string) => (dispatch: any) => {
	dispatch({ type: "DELETE_FORM_REQUEST" });
	axios.delete(`${process.env.REACT_APP_API_ENDPOINT_V2}forms/${id}`, { headers: headers() })
		.then(() => { dispatch({ type: "DELETE_FORM_SUCCESS", response: { status: 204 }, resource: { id } }); })
		.catch((e) => { dispatch({ type: "DELETE_FORM_FAILED", response: e }); });
});

// Uploads a file to S3 using the record's presigned_post_url; returns the S3 Key.
// Field order mirrors the Documents upload flow to keep S3 CORS / signature behaviour identical.
// `options` feeds the upload tray (actions/documents#startUploadTray).
export const uploadFormFileToS3 = async (
	presigned: { url: string; fields: Record<string, string> },
	file: File,
	options: { cancelToken?: any; onUploadProgress?: (event: any) => void } = {}
): Promise<string> => {
	const { fields } = presigned;
	const data = new FormData();
	data.append("key", fields["key"]);
	if (fields["Expires"]) data.append("Expires", fields["Expires"]);
	data.append("success_action_status", fields["success_action_status"]);
	data.append("policy", fields["policy"]);
	data.append("x-amz-credential", fields["x-amz-credential"]);
	data.append("x-amz-algorithm", fields["x-amz-algorithm"]);
	data.append("x-amz-date", fields["x-amz-date"]);
	data.append("x-amz-signature", fields["x-amz-signature"]);
	if (fields["acl"]) data.append("acl", fields["acl"]);
	data.append("file", file);
	const res = await axios.post(presigned.url, data, {
		cancelToken: options.cancelToken,
		onUploadProgress: options.onUploadProgress,
	});
	if (res.status < 200 || res.status >= 300) {
		throw new Error(`S3 upload returned status ${res.status}`);
	}
	const body = typeof res.data === "string" ? res.data : "";
	const xml = new window.DOMParser().parseFromString(body, "text/xml");
	const keyNode = xml.getElementsByTagName("Key")[0];
	// Prefer the key echoed back by S3; fall back to the signed `key` field we sent.
	const parsedKey = keyNode?.innerHTML?.trim();
	return parsedKey || fields["key"];
};
